import Image from 'next/image'
import Link from 'next/link'
import SEO from '../components/SEO'
import styles from '../styles/CarRental.module.css'
import { testimonials } from '../const/home'
import { 
    workflowIssues, 
    builtForList, 
    stats, 
    capabilities,
} from '../const/car-rental'
import { questions } from '../const/faq'
import language from '../languages/insurance.json'
import { useState,useRef,useEffect } from 'react'
import ContactUsCTA from '../components/ContactUsCTA' 
import BlogCarousel from '../components/home/BlogCarousel'

export default function CarRental({locale}) {

  const [posts, setPosts] = useState([]);
  const [testimonial, setTestimonial] = useState({});

  const scrollRef = useRef();


  const scroll = (direction) => {
    const container = scrollRef.current;
    const cardWidth = container.querySelector(`.${styles.blogItem}`).offsetWidth + 20; // Include margin/padding

    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };
    const QuestionSection = ({ keyIndex, que, ans }) => {
        const [isOpen, setIsOpen] = useState(false);
    
        return (
          <div
            className={`${styles.questionModal} ${isOpen ? styles.openModal : ""}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <p className={styles.questionIndex}>
              {String(keyIndex + 1).padStart(2, "0")}
            </p>
            <div className={styles.question}>
              <p>{que}</p>
              <Image
                src={isOpen ? "/img/shrink-icon.svg" : "/img/expand-icon.svg"}
                alt="FAQ"
                width={36}
                height={36}
                objectFit="contain"
              />
            </div>
            <div className={`${styles.answer} ${isOpen ? styles.open : ""}`}>
              {ans.map((item, index) => (
                <p key={index}>{item}</p>
              ))}
            </div>
          </div>
        );
      };

    return (
        <div className={styles.container}>

            <SEO
                title="Rental Car Inspection Software to Detect Damage Instantly"
                description="Detect rental car damage instantly with AI-powered inspections. Reduce disputes, improve transparency, and automate pre and post rental checks."
                image="https://inspektlabs.com/img/car.png"
                url="https://inspektlabs.com/car-rental"
                page="car-rental"
            />

            <main className={styles.main}>

            {/* Hero Section */}
            <section className={styles.heroSection}>
                <div className={styles.heroContent}>
                    <h1 className={styles.heroTitle}>Car Rental Inspection App to Automate Check-In/Check-Out Damage Detection</h1>
                    <p className={styles.heroText}>
                    Automate vehicle inspections for car rental & leasing companies to help cut down on inspection time, improve accuracy, and protect vehicle value
                    </p>
                    <ContactUsCTA as="div" cluster = "car-rental" className={styles.heroBtn}>
                        Request a Demo
                        <Image src='/img/arrow right white.png' alt="Arrow" width={20} height={20} />
                    </ContactUsCTA> 
                    
                </div>
                <div className={styles.heroImageSection}>
                    <Image 
                        src='/img/Car Rental Main Img.png'
                        alt='Car Rental Main Img'
                        layout='fill'
                        objectFit='contain'
                        objectPosition='center'
                        sizes='(max-width: 1024px) 16px, 50vw'
                        priority
                    />
                </div>
            </section>

            {/* Why Manual Inspection Issues Section */}
            <section className={styles.workflowSection}>
                <div className={styles.workflowContent}>
                    <h2 className={styles.sectionHeading}>
                    How Our Car Rental Inspection App Works
                    </h2>
                    <div className={styles.workflowGrid}>
                        {workflowIssues.map((issue, idx) => (
                            <div className={styles.workflowCard} key={idx}>
                                <div className={styles.workflowIcon}>
                                    <Image 
                                        src={issue.icon} 
                                        alt={issue.title} 
                                        width={70} 
                                        height={65} 
                                    />
                                </div>
                                <h3 className={styles.workflowCardTitle}>{issue.title}</h3>
                                <hr className={styles.workflowCardLine} />
                            </div>
                        ))}
                    </div>
                    <p className={styles.automationText}>
                        These inefficiencies add up to significant costs and lost opportunities
                    </p>
                </div>
            </section>

            {/* Built For Section */}
            <section className={styles.builtForSection}>
                <div className={styles.builtForContent}>
                    <p className={styles.capabilitiesHeading}>CAPABILITIES</p>
                    <h2 className={styles.sectionHeading}>
                    Key Features for Rental & Leasing Companies
                    </h2>
                    <div className={styles.builtForGrid}>
                        {builtForList.map((item, idx) => (
                            <div className={styles.builtForCard} key={idx}>
                                <div className={styles.builtForIcon}>
                                    <Image 
                                        src={item.icon} 
                                        alt={item.title} 
                                        width={159} 
                                        height={158} 
                                    />
                                </div>
                                <h3 className={styles.builtForCardTitle}>{item.title}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className={styles.statsSection}>
                <div className={styles.statsContainer}>
                    <h2 className={styles.statsTitle}>
                        How we improve your operational efficiency
                    </h2>
                    <div className={styles.statsGrid}>
                        {stats.map((stat, index) => (
                            <div key={index} className={styles.statItem}>
                                <h3 className={styles.statValue}>{stat.value}</h3>
                                <p className={styles.statLabel}>{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className={styles.capabilitiesSection}>
                <div className={styles.capabilitiesContent}>
                    <h2 className={styles.sectionHeading}>
                    Rental Car Inspection Checklist — Automated
                    </h2>
                    <p className={styles.capabilitiesSubtitle}>Every rental and lease vehicle runs through the same checks at handover. Our guide to <a className={styles.showClick} target='_blank' href="https://inspektlabs.com/blog/automation-of-car-inspections-for-car-rental-industry/">car rental inspection automation</a> shows how to run those checks from a smartphone, with a timestamped condition report at check-in and check-out.</p>
                    <div className={styles.capabilitiesGrid}>
                        {capabilities.map((capability, idx) => (
                            <div className={styles.capabilityCard} key={idx}>
                                <div className={styles.capabilityIcon}>
                                    <Image 
                                        src={capability.icon} 
                                        alt={capability.title} 
                                        width={170} 
                                        height={120} 
                                    />
                                </div>
                                <div className={styles.capabilityContent}>
                                    <h3 className={styles.capabilityTitle}>{capability.title}</h3>
                                    <p className={styles.capabilityText}>{capability.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog Section */}
            
            <BlogCarousel
                page="car-rental"
                heading="Why Leading Automotive industries Choose Inspektlabs - Blogs"
                subHeading="Stay updated with the latest trends, best practices and insights from the automotive inspection industry."
            />

            {/* FAQ Section */}
            <section className={styles.faqSectionContainer}>
                <div className={styles.faqSection}>
                    <div className={styles.faqTitleContainer}>
                    <h2 className={styles.faqTitle}>Commonly asked questions</h2>
                    </div>
                    <div className={styles.questionContainer}>
                    <div className={styles.faqGrid}>
                        {questions["Car Rental"][locale].map((item, index) => (
                        <QuestionSection keyIndex={index} que={item.Q} ans={item.A} />
                        ))}
                    </div>
                    </div>
                    <Link href="/faq">
                    <a
                    className={styles.viewMoreBtn}
                    style={{ width: "200px" }}
                    >
                    View More
                    </a>
                    </Link>
                </div>
                </section>

            </main>

        </div>
    )
}